export const MAX_IMAGES_PER_PAGE = 3;
export const DEFAULT_PAGE_IMAGE_COUNT = 3;

export function normalizeImageCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_PAGE_IMAGE_COUNT;
  return Math.max(0, Math.min(MAX_IMAGES_PER_PAGE, Math.round(n)));
}

export function createPageImageBlock(imageCount = DEFAULT_PAGE_IMAGE_COUNT, imageIds = []) {
  const count = normalizeImageCount(imageCount);
  const ids = Array.isArray(imageIds) ? imageIds.slice(0, MAX_IMAGES_PER_PAGE) : [];
  while (ids.length < count) ids.push(null);
  return { imageCount: count, imageIds: ids };
}

export function migrateFlatPageImages(flatImages, imagesPerChunk = MAX_IMAGES_PER_PAGE) {
  if (!Array.isArray(flatImages) || !flatImages.length) return [];
  const blocks = [];
  for (let i = 0; i < flatImages.length; i += imagesPerChunk) {
    const chunk = flatImages.slice(i, i + imagesPerChunk);
    blocks.push(createPageImageBlock(imagesPerChunk, chunk.map((id) => id || null)));
  }
  return blocks;
}

export function mapApiBlockToForm(block) {
  if (!block || typeof block !== "object") return createPageImageBlock();
  return createPageImageBlock(block.image_count ?? block.imageCount, block.image_ids ?? block.imageIds ?? []);
}

export function mapFormBlockToApi(block) {
  const normalized = createPageImageBlock(block?.imageCount, block?.imageIds ?? []);
  return {
    image_count: normalized.imageCount,
    image_ids: normalized.imageIds,
  };
}

export function flattenPageImageBlocks(blocks) {
  const flat = [];
  (blocks || []).forEach((block) => {
    const normalized = createPageImageBlock(block?.imageCount, block?.imageIds ?? []);
    for (let i = 0; i < normalized.imageCount; i++) {
      flat.push(normalized.imageIds[i] || null);
    }
  });
  return flat;
}

export function collectImageIdsFromBlocks(blocks) {
  const ids = new Set();
  (blocks || []).forEach((block) => {
    (block?.imageIds || block?.image_ids || []).forEach((id) => {
      if (id) ids.add(id);
    });
  });
  return [...ids];
}

export function ensurePageImageBlocksLength(blocks, pageCount, defaultCount = DEFAULT_PAGE_IMAGE_COUNT) {
  const result = (blocks || []).map((block) => createPageImageBlock(block?.imageCount, block?.imageIds ?? block?.image_ids ?? []));
  while (result.length < pageCount) {
    result.push(createPageImageBlock(defaultCount, []));
  }
  return result;
}

export function getPageImageCount(blocks, pageIndex, defaultCount = DEFAULT_PAGE_IMAGE_COUNT) {
  const block = blocks?.[pageIndex];
  if (block == null) return defaultCount;
  if (block.imageCount != null || block.image_count != null) {
    return normalizeImageCount(block.imageCount ?? block.image_count);
  }
  return defaultCount;
}

export function setPageImageCount(blocks, pageIndex, imageCount, pageCount = pageIndex + 1) {
  const result = ensurePageImageBlocksLength(blocks, pageCount);
  const count = normalizeImageCount(imageCount);
  const existing = result[pageIndex]?.imageIds || [];
  const imageIds = [...existing];
  while (imageIds.length < count) imageIds.push(null);
  result[pageIndex] = { imageCount: count, imageIds };
  return result;
}

export function mergePageImageBlocks(storedBlocks, fallbackBlocks) {
  const stored = Array.isArray(storedBlocks) ? storedBlocks : [];
  const fallback = Array.isArray(fallbackBlocks) ? fallbackBlocks : [];
  const maxLen = Math.max(stored.length, fallback.length);
  const merged = [];

  for (let index = 0; index < maxLen; index++) {
    const storedBlock = stored[index];
    if (storedBlock != null && typeof storedBlock === "object") {
      merged.push(
        createPageImageBlock(
          storedBlock.imageCount ?? storedBlock.image_count,
          storedBlock.imageIds ?? storedBlock.image_ids ?? []
        )
      );
      continue;
    }
    if (fallback[index]) {
      merged.push(createPageImageBlock(fallback[index].imageCount, fallback[index].imageIds ?? []));
      continue;
    }
    merged.push(createPageImageBlock());
  }

  return merged;
}

export function setPageImageAtSlot(blocks, pageIndex, slotIndex, imageId, pageCount = pageIndex + 1) {
  const result = ensurePageImageBlocksLength(blocks, pageCount);
  const block = result[pageIndex] || createPageImageBlock();
  const count = normalizeImageCount(block.imageCount);
  const imageIds = [...(block.imageIds || [])];
  while (imageIds.length < count) imageIds.push(null);
  if (slotIndex >= 0 && slotIndex < count) {
    imageIds[slotIndex] = imageId;
  }
  result[pageIndex] = { imageCount: count, imageIds };
  return result;
}
